import axios from 'axios';
import cacheService from './cacheService';
import config from '../config';

// Normalize base URL to always end with a slash so relative paths like
// "financial-engine/wip/" resolve to ".../api/financial-engine/wip/".
const baseURL = config.apiBaseUrl.endsWith('/') ? config.apiBaseUrl : `${config.apiBaseUrl}/`;
const isCypressTestRun = typeof window !== 'undefined' && Boolean(window.Cypress);

/**
 * Decode JWT user_id for cache scoping (matches SIMPLE_JWT USER_ID_CLAIM).
 *
 * @param {string|null} token - Bearer access token.
 * @returns {string|null} User id string when present in payload.
 */
const decodeJwtUserId = (token) => {
    if (!token || typeof token !== 'string') {
        return null;
    }
    try {
        const payloadBase64 = token.split('.')[1];
        if (!payloadBase64) {
            return null;
        }
        const normalized = payloadBase64.replace(/-/g, '+').replace(/_/g, '/');
        const payload = JSON.parse(atob(normalized));
        const userId = payload.user_id ?? payload.sub;
        return userId != null ? String(userId) : null;
    } catch {
        return null;
    }
};

/**
 * Scope API cache keys per authenticated user so sessions cannot bleed across logins.
 *
 * @returns {string} Cache scope suffix.
 */
export const getCacheUserScope = () => {
    if (typeof window === 'undefined') {
        return 'anon';
    }
    const token = localStorage.getItem('token');
    if (!token) {
        return 'anon';
    }
    const userId = decodeJwtUserId(token);
    if (userId) {
        return `u${userId}`;
    }
    return `t${token.slice(-12)}`;
};

const scopedCacheKey = (config) => `${cacheService.generateKey(config)}:${getCacheUserScope()}`;

const normalizeRequestUrl = (url) => {
    if (typeof url !== 'string') return url;
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    if (url === '/api') return '';
    if (url === 'api') return '';
    if (url === '/api/') return '';
    if (url.startsWith('/api/')) return url.slice(4);
    if (url.startsWith('api/')) return url.slice(4);
    return url.replace(/^\/+/, '');
};

const isNotificationPreferencesRequest = (error) => {
    const requestUrl = error?.config?.url || error?.config?.baseURL || '';
    return typeof requestUrl === 'string' && requestUrl.includes('notifications/preferences');
};

const isCanceledRequestError = (error) => {
    const code = error?.code;
    const name = error?.name;
    const msg = typeof error?.message === 'string' ? error.message.toLowerCase() : '';
    return (
        code === 'ERR_CANCELED' ||
        name === 'CanceledError' ||
        msg === 'canceled'
    );
};

const apiClient = axios.create({
    baseURL,
    headers: { 'Content-Type': 'application/json' }
});

// -----------------------------------------------------------------------------
// Access-token refresh (module scope, runs before any React effect).
// AuthProvider previously registered a 401 interceptor in useEffect; child
// dashboard effects run first, so executive/financial requests could 401 before
// the interceptor existed. Refresh here so parallel requests dedupe and retry.
// -----------------------------------------------------------------------------
let refreshInFlight = null;

function refreshAccessToken() {
    if (refreshInFlight) {
        return refreshInFlight;
    }
    refreshInFlight = (async () => {
        if (typeof window === 'undefined') {
            throw new Error('No window');
        }
        const refresh = localStorage.getItem('refreshToken');
        if (!refresh) {
            throw new Error('No refresh token');
        }
        const { data } = await axios.post(
            `${baseURL}auth/token/refresh/`,
            { refresh },
            { headers: { 'Content-Type': 'application/json' } }
        );
        const access = data.access;
        localStorage.setItem('token', access);
        window.dispatchEvent(new CustomEvent('auth:token-refreshed', { detail: { access } }));
        return access;
    })().finally(() => {
        refreshInFlight = null;
    });
    return refreshInFlight;
}

// Cache configuration for different endpoints
const CACHE_CONFIG = {
    // User data - cache longer
    '/users/me/': { duration: 'long', storage: 'localStorage' },
    '/departments/': { duration: 'long', storage: 'localStorage' },

    // Project data - medium cache
    '/projects/': { duration: 'medium', storage: 'sessionStorage' },
    '/projects/resource_planning/': { duration: 'short', storage: 'memory' },

    // Reference data - long cache
    '/expense-categories/': { duration: 'long', storage: 'localStorage' },

    // Dynamic data - short cache or no cache
    '/timesheets/': { duration: 'short', storage: 'memory' },
    '/tasks/': { duration: 'short', storage: 'sessionStorage' },
    '/time-entries/': { duration: 'short', storage: 'sessionStorage' },
    '/expense-reports/': { duration: 'short', storage: 'memory' },
    '/projects/*/health-assessment/': { duration: 'short', storage: 'memory' },

    // Financial engine - short cache (2-5 min) to reduce load
    'financial-engine/dashboard/': { duration: 'short', storage: 'memory' },
    'financial-engine/portfolio-health/': { duration: 'short', storage: 'memory' },
    'financial-engine/revenue-forecast/': { duration: 'short', storage: 'memory' },
    'financial-engine/dashboard-summary/': { duration: 'short', storage: 'memory' },
    'financial-engine/executive-summary/': { duration: 'short', storage: 'memory' },
    'financial-engine/bill-rates/': { duration: 'short', storage: 'memory' },
    'financial-engine/backlog/': { duration: 'short', storage: 'memory' },
    'financial-engine/realization/': { duration: 'short', storage: 'memory' },
    'financial-engine/wip/': { duration: 'short', storage: 'memory' },
    'financial-engine/contribution-margin/': { duration: 'short', storage: 'memory' },
    'financial-engine/ar-aging/': { duration: 'short', storage: 'memory' },
    'financial-engine/cash-flow/': { duration: 'short', storage: 'memory' },
    'financial-engine/enhanced-forecast/': { duration: 'short', storage: 'memory' },
    'financial-engine/revenue-triad/': { duration: 'short', storage: 'memory' },
    'analytics-engine/expense-ratios/': { duration: 'short', storage: 'memory' },
    'analytics-engine/benchmark-compare/': { duration: 'short', storage: 'memory' },
    'resource-optimization/utilization/': { duration: 'short', storage: 'memory' },
    'resource-optimization/': { duration: 'short', storage: 'memory' },
    'analytics/kpis/': { duration: 'short', storage: 'memory' },
    'analytics/alerts/': { duration: 'short', storage: 'memory' },
};

export const clearApiCache = (endpoint) => {
    if (!endpoint) {
        cacheService.clear(null);
        return;
    }
    // Interceptor keys look like `GET:timesheets/?page=1`; getCached keys look like
    // `/timesheets/-{"page":1}`. Strip slashes so `timesheets` matches both.
    const pattern = endpoint.replace(/^\/+|\/+$/g, '');
    cacheService.clear(pattern || null);
};

/** Drop cached billing queue and AR snapshots after invoice/payment mutations. */
export const clearBillingWorkflowCaches = () => {
    clearApiCache('projects/billing-interventions');
    clearApiCache('financial-engine/ar-aging');
};

/** Clear user-scoped caches after login/logout to prevent cross-session data bleed. */
export const clearAuthScopedCaches = () => {
    clearApiCache('/projects/resource_planning/');
    clearApiCache('/projects/');
    clearApiCache('/users/me/');
    clearApiCache('project-operations/');
    clearApiCache('projects/portfolio-attention/');
    cacheService.clear(null, 'memory');
};

const SLOW_REQUEST_THRESHOLD_MS = 3000;
const SLOW_REQUEST_THRESHOLDS_BY_PREFIX = {
    'financial-engine/executive-summary/': 4000,
};

const inFlightMap = new Map();

function getDedupeKey(config) {
    const method = (config.method || 'GET').toUpperCase();
    const cacheConfig = getCacheConfigForUrl(config.url, config.method);
    if (method !== 'GET' && !cacheConfig) return null;
    return cacheService.generateKey(config);
}

const baseAdapter =
    typeof apiClient.defaults.adapter === 'function'
        ? apiClient.defaults.adapter
        : typeof axios.getAdapter === 'function'
            ? axios.getAdapter(apiClient.defaults.adapter)
            : null;

if (typeof baseAdapter === 'function') {
    apiClient.defaults.adapter = async (config) => {
        if (config.cachedResponse) {
            return {
                data: config.cachedResponse,
                status: 200,
                statusText: 'OK',
                headers: {},
                config,
                request: null
            };
        }

        const key = getDedupeKey(config);
        if (key) {
            const existing = inFlightMap.get(key);
            if (existing) {
                return existing.promise;
            }
            const promise = baseAdapter(config);
            inFlightMap.set(key, { promise });
            promise.finally(() => {
                inFlightMap.delete(key);
            });
            return promise;
        }
        return baseAdapter(config);
    };
}

apiClient.interceptors.request.use(
    (config) => {
        config.url = normalizeRequestUrl(config.url);
        config.requestStartTime = performance.now();
        const requestFilters = config.params || (config.method?.toLowerCase() === 'post' ? config.data : undefined);
        const endpoint = (config.url || '').replace(/^\/+|\/+$/g, '');
        if (typeof window !== 'undefined') {
            const requestLog = window.__apiRequestLog || [];
            requestLog.push({
                type: 'request',
                method: config.method?.toUpperCase(),
                url: `${config.baseURL || ''}${config.url}`,
                endpoint,
                source_tag: endpoint ? `api/${endpoint}` : 'unknown',
                filters: requestFilters || null,
                requestedAt: new Date().toISOString()
            });
            window.__apiRequestLog = requestLog.slice(-25);
        }
        const token = localStorage.getItem('token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }

        // Check cache for idempotent GETs and explicitly configured computed POSTs.
        const cacheConfig = getCacheConfigForUrl(config.url, config.method);
        if (!isCypressTestRun && cacheConfig && ['GET', 'POST'].includes(config.method?.toUpperCase())) {
            const cacheKey = scopedCacheKey(config);
            const cachedData = cacheService.get(cacheKey, cacheConfig.storage);
            if (cachedData) {
                // Reduced logging - only for cache busting URLs
                if (process.env.NODE_ENV === 'development' && !config.url?.includes('_t=')) {
                    console.log(`✓ Cache: ${config.url}`);
                }
                config.cachedResponse = cachedData;
                config.cacheKey = cacheKey;
                config.cacheConfig = cacheConfig;
            }
        }

        if (!config.cachedResponse) {
            const dedupeKey = getDedupeKey(config);
            const isDuplicateInFlight = dedupeKey && inFlightMap.has(dedupeKey);
            if (!isDuplicateInFlight && process.env.REACT_APP_API_DEBUG === 'true') {
                console.log(
                    'API Request:',
                    config.method?.toUpperCase(),
                    config.url,
                    config.params ? { params: config.params } : ''
                );
            }
        }

        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

apiClient.interceptors.response.use(
    (response) => {
        const start = response.config?.requestStartTime;
        const requestLog = response.config ? {
            endpoint: (response.config.url || '').replace(/^\/+|\/+$/g, ''),
            source_tag: response.config.url ? `api/${(response.config.url || '').replace(/^\/+|\/+$/g, '')}` : 'unknown'
        } : null;
        if (typeof start === 'number') {
            const durationMs = performance.now() - start;
            const slowThreshold = Object.entries(SLOW_REQUEST_THRESHOLDS_BY_PREFIX).find(
                ([prefix]) => (response.config?.url || '').includes(prefix)
            )?.[1] ?? SLOW_REQUEST_THRESHOLD_MS;
            if (durationMs > slowThreshold && process.env.NODE_ENV === 'development') {
                console.warn(`[Slow API] ${response.config?.url} took ${durationMs.toFixed(0)}ms (threshold: ${slowThreshold}ms)`);
            }
            if (typeof window !== 'undefined' && requestLog) {
                const responseLog = {
                    type: 'response',
                    endpoint: requestLog.endpoint,
                    source_tag: requestLog.source_tag,
                    method: response.config?.method?.toUpperCase(),
                    url: `${response.config?.baseURL || ''}${response.config?.url || ''}`,
                    status: response.status,
                    durationMs,
                    calculatedAt: new Date().toISOString()
                };
                const payload = response.data || {};
                const dataQuality = payload.data_quality || payload?.kpi_metadata?.data_quality || payload?.metadata?.data_quality || null;
                if (dataQuality) {
                    responseLog.calculation_complete = dataQuality.calculation_complete === true;
                    responseLog.required_data = dataQuality.required_data || [];
                    responseLog.warnings = dataQuality.warnings || [];
                    responseLog.fallback_policy = dataQuality.fallback_policy || null;
                    responseLog.as_of = dataQuality.as_of || payload.as_of || null;
                }
                const responseAuditLog = window.__apiRequestLog || [];
                responseAuditLog.push(responseLog);
                window.__apiRequestLog = responseAuditLog.slice(-25);
            }
        }
        if (response.config.cachedResponse) {
            return {
                ...response,
                data: response.config.cachedResponse,
                cached: true
            };
        }

        // Cache successful GET responses and explicitly configured computed POST responses.
        if (['GET', 'POST'].includes(response.config.method?.toUpperCase()) && response.status === 200) {
            const cacheConfig = getCacheConfigForUrl(response.config.url, response.config.method);
            if (!isCypressTestRun && cacheConfig) {
                const cacheKey = scopedCacheKey(response.config);
                cacheService.set(cacheKey, response.data, cacheConfig.duration, cacheConfig.storage);
            }
        }

        invalidateCachesAfterMutation(response.config?.url, response.config?.method);

        return response;
    },
    async (error) => {
        if (isCanceledRequestError(error)) {
            return Promise.reject(error);
        }

        const originalRequest = error.config;
        const status = error.response?.status;
        const reqUrl = typeof originalRequest?.url === 'string' ? originalRequest.url : '';

        if (
            status === 401 &&
            originalRequest &&
            !originalRequest._retry &&
            reqUrl &&
            !reqUrl.includes('auth/token')
        ) {
            originalRequest._retry = true;
            try {
                const newToken = await refreshAccessToken();
                originalRequest.headers = originalRequest.headers || {};
                originalRequest.headers.Authorization = `Bearer ${newToken}`;
                return apiClient(originalRequest);
            } catch {
                if (typeof window !== 'undefined') {
                    localStorage.removeItem('token');
                    localStorage.removeItem('refreshToken');
                    localStorage.removeItem('user');
                    window.dispatchEvent(new Event('auth:logout'));
                    const currentPath = window.location.pathname;
                    if (currentPath !== '/login' && currentPath !== '/register') {
                        window.location.href = `/login?returnTo=${encodeURIComponent(currentPath)}`;
                    }
                }
                return Promise.reject(error);
            }
        }

        const start = error.config?.requestStartTime;
        if (typeof start === 'number') {
            const durationMs = performance.now() - start;
            const slowThreshold = Object.entries(SLOW_REQUEST_THRESHOLDS_BY_PREFIX).find(
                ([prefix]) => (error.config?.url || '').includes(prefix)
            )?.[1] ?? SLOW_REQUEST_THRESHOLD_MS;
            if (durationMs > slowThreshold && process.env.NODE_ENV === 'development') {
                console.warn(`[Slow API] ${error.config?.url} failed after ${durationMs.toFixed(0)}ms`);
            }
            if (typeof window !== 'undefined') {
                const responseLog = {
                    type: 'error',
                    endpoint: (error.config?.url || '').replace(/^\/+|\/+$/g, ''),
                    source_tag: error.config?.url ? `api/${(error.config.url || '').replace(/^\/+|\/+$/g, '')}` : 'unknown',
                    method: error.config?.method?.toUpperCase(),
                    url: `${error.config?.baseURL || ''}${error.config?.url || ''}`,
                    status: error.response?.status || null,
                    durationMs,
                    calculatedAt: new Date().toISOString(),
                    error: error.message || error.code || 'Unknown API error'
                };
                const errorLog = window.__apiRequestLog || [];
                errorLog.push(responseLog);
                window.__apiRequestLog = errorLog.slice(-25);
            }
        }
        if (process.env.NODE_ENV === 'development') {
            const statusDev = error.response?.status;
            const url = error.config?.url ?? error.config?.baseURL;
            const fullUrl = error.config ? (error.config.baseURL || '') + (error.config.url || '') : '';
            const msg = error.message || error.code || '';
            const isPreferencesEndpointMissing = isNotificationPreferencesRequest(error) && statusDev === 404;
            if (!isPreferencesEndpointMissing) {
                console.error('API Error:', statusDev ?? 'no response', url ?? 'no config', fullUrl ? `(${fullUrl})` : '', msg ? String(msg) : '');
            }
        }
        return Promise.reject(error);
    }
);

// POST is cached only for explicitly computed read endpoints (e.g. financial forecasts).
const POST_CACHE_URL_PREFIXES = [
    'financial-engine/',
    'analytics-engine/',
    'resource-optimization/',
];

function isTimesheetMutationUrl(url) {
    if (!url || typeof url !== 'string') return false;
    return /timesheets\/\d+\/(approve|reject|submit|flag_concern|resolve_escalation|change_status_after_rejection)\/?/.test(url);
}

function isPostCacheEligible(url) {
    if (!url || typeof url !== 'string') return false;
    const normalized = url.replace(/^\/+/, '');
    return POST_CACHE_URL_PREFIXES.some((prefix) => normalized.startsWith(prefix));
}

function invalidateCachesAfterMutation(url, method) {
    const normalized = (url || '').replace(/^\/+/, '');
    const verb = (method || '').toUpperCase();
    if (!normalized || verb === 'GET') {
        return;
    }
    if (
        normalized.includes('invoices/')
        && ['POST', 'PATCH', 'PUT', 'DELETE'].includes(verb)
    ) {
        clearBillingWorkflowCaches();
    }
}

// Helper function to get cache config for a URL
function getCacheConfigForUrl(url, method = 'GET') {
    if (!url) return null;

    // Remove base URL if present
    const cleanUrl = url.replace('/api', '').replace(/^\/+/, '/');
    const normalizedCleanUrl = cleanUrl.startsWith('/') ? cleanUrl : `/${cleanUrl}`;
    const normalizedUrl = cleanUrl.replace(/^\/+/, '');

    if (isTimesheetMutationUrl(normalizedUrl)) {
        return null;
    }

    if (method?.toUpperCase() === 'POST' && !isPostCacheEligible(normalizedUrl)) {
        return null;
    }

    // Dynamic project staffing/assignment endpoints should never be cached to avoid
    // stale reads right after allocation changes.
    if (
        cleanUrl.includes('/resources-optimized/') ||
        cleanUrl.includes('/resource-suggestions/') ||
        cleanUrl.includes('/resource-suggestions/?')
    ) {
        return null;
    }

    // Control-center snapshots are interactive and must reflect server-side
    // state transitions (e.g. change order approvals) immediately.
    if (cleanUrl.includes('/control-center/')) {
        return null;
    }

    // Billing queue rows change when invoices are created, sent, or paid.
    if (cleanUrl.includes('/billing-interventions')) {
        return null;
    }

    // Check for exact matches first
    if (CACHE_CONFIG[cleanUrl]) {
        return CACHE_CONFIG[cleanUrl];
    }
    if (CACHE_CONFIG[normalizedCleanUrl]) {
        return CACHE_CONFIG[normalizedCleanUrl];
    }

    // Check for pattern matches
    for (const [pattern, config] of Object.entries(CACHE_CONFIG)) {
        const normalizedPattern = pattern.replace(/\/$/, '');
        if (normalizedPattern.includes('*')) {
            const escapedPattern = normalizedPattern.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            const wildcardPattern = escapedPattern.replace(/\\\*/g, '[^/]+');
            const matcher = new RegExp(`^${wildcardPattern}(\\/|$)`);
            if (matcher.test(cleanUrl) || matcher.test(normalizedCleanUrl)) {
                return config;
            }
            continue;
        }

        if (
            cleanUrl.startsWith(normalizedPattern) ||
            normalizedCleanUrl.startsWith(normalizedPattern)
        ) {
            return config;
        }
    }

    return null;
}

// Add utility methods from old apiClient for compatibility
apiClient.getWithRetry = async function (url, config = {}, retries = 3, backoff = 300) {
    for (let i = 0; i < retries; i++) {
        try {
            return await this.get(url, config);
        } catch (error) {
            if (i === retries - 1) throw error;
            await new Promise(resolve => setTimeout(resolve, backoff * 2 ** i));
        }
    }
};

// API utilities for compatibility with old apiClient
export const apiUtils = {
    getCached: async (url, params = {}, cacheDuration = 300000) => {
        if (isCypressTestRun) {
            return apiClient.get(url, { params });
        }
        const cacheKey = `${url}-${JSON.stringify(params)}`;
        const cached = cacheService.get(cacheKey, 'memory');

        if (cached) {
            return { data: cached };
        }

        const response = await apiClient.get(url, { params });
        cacheService.set(cacheKey, response.data, 'short', 'memory');
        return response;
    },

    clearCache: (url) => {
        clearApiCache(url);
    },

    clearBillingWorkflowCaches,
};

export default apiClient; 